// components/PaywallScreen.jsx
// =============================================================================
// GENEROSITY WTR APP - WTR+ PAYWALL (Commit 6)
//
// The conversion surface. Spec (ratified 2026-07-13):
//   Header: WTR+
//   Lead:   "Absorption, not just intake."
//   Price:  $5.99/mo | $59.99/yr toggle, ANNUAL DEFAULT, framed "2 months free"
//   Footer: hardware purchases include 12 months of WTR+
//
// Checkout wiring is Commit 8 (Stripe on web, StoreKit in native). Until then
// the CTA is honest about it and routes purchase intent to the Intelligent
// WTR Bottle on Shopify, which really does include 12 months of WTR+ today
// via the orders/paid webhook. No fake checkout, no dead button.
//
// The three USPTO differentiation phrases appear verbatim: this is a
// Hydration Intelligence surface.
// =============================================================================

import { useState } from "react";

const C = {
  white: "#FFFFFF", border: "#E8EEF2", blue: "#51B0E6", blueDark: "#2A8FCA",
  blueLight: "#EDF6FC", body: "#1A2535", muted: "#6B7A8D", cardGray: "#F0F1F3",
};

const BOTTLE_URL =
  "https://generositywtr.myshopify.com/products/intelligent-wtr-bottle" +
  "?utm_source=wtr-app&utm_medium=paywall&utm_campaign=wtr-plus&utm_content=includes-12mo";

const FEATURES = [
  ["Hydration Score", "Your absorption efficiency model, not a concentration adjustment model, with an evening projection."],
  ["Absorption Intelligence", "What your body kept, not just what you drank."],
  ["Wearable fusion", "Oura and biometrics folded into your hydration."],
  ["Adaptive daily target", "Adaptive range, not a fixed threshold."],
  ["90-day trends", "Your full history, not just this week."],
  ["Bottle and Hub connectivity", "Verified intake from your Generosity hardware."],
];

export default function PaywallScreen({ onClose }) {
  const [plan, setPlan] = useState("annual"); // annual default, deliberate
  const [notice, setNotice] = useState(false);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 700, background: "rgba(16,24,34,0.45)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={() => onClose && onClose()}>
      <div onClick={(e) => e.stopPropagation()} data-testid="paywall"
        style={{ width: "100%", maxWidth: 480, maxHeight: "92vh", overflowY: "auto", background: C.white, borderRadius: "24px 24px 0 0", padding: "22px 20px 28px", fontFamily: "Montserrat, -apple-system, sans-serif", color: C.body }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2.5, color: C.blue, textTransform: "uppercase" }}>WTR+</div>
          <button onClick={() => onClose && onClose()} aria-label="Close" data-testid="paywall-close"
            style={{ background: C.cardGray, border: "none", borderRadius: 999, width: 32, height: 32, fontSize: 15, fontWeight: 800, color: C.muted, cursor: "pointer" }}>
            {"\u2715"}
          </button>
        </div>

        <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.25, marginBottom: 16 }}>
          Absorption, not just intake.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {FEATURES.map(([t, d]) => (
            <div key={t} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <div style={{ width: 18, height: 18, borderRadius: 999, background: C.blueLight, color: C.blueDark, fontSize: 11, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{"\u2713"}</div>
              <div>
                <span style={{ fontSize: 13.5, fontWeight: 800 }}>{t}</span>
                <span style={{ fontSize: 13, color: C.muted }}> {"\u00B7"} {d}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Price toggle — annual default */}
        <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
          <button onClick={() => setPlan("annual")} data-testid="paywall-annual"
            style={{ flex: 1, textAlign: "left", background: plan === "annual" ? C.blueLight : C.white, border: `2px solid ${plan === "annual" ? C.blue : C.border}`, borderRadius: 16, padding: "13px 14px", cursor: "pointer", fontFamily: "inherit", position: "relative" }}>
            <div style={{ position: "absolute", top: -9, left: 12, background: C.blue, color: "#fff", fontSize: 10, fontWeight: 800, letterSpacing: 0.5, borderRadius: 999, padding: "2px 10px", textTransform: "uppercase" }}>2 months free</div>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>Annual</div>
            <div style={{ fontSize: 19, fontWeight: 800 }}>$59.99<span style={{ fontSize: 12, color: C.muted, fontWeight: 700 }}>/yr</span></div>
          </button>
          <button onClick={() => setPlan("monthly")} data-testid="paywall-monthly"
            style={{ flex: 1, textAlign: "left", background: plan === "monthly" ? C.blueLight : C.white, border: `2px solid ${plan === "monthly" ? C.blue : C.border}`, borderRadius: 16, padding: "13px 14px", cursor: "pointer", fontFamily: "inherit" }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>Monthly</div>
            <div style={{ fontSize: 19, fontWeight: 800 }}>$5.99<span style={{ fontSize: 12, color: C.muted, fontWeight: 700 }}>/mo</span></div>
          </button>
        </div>

        <button onClick={() => setNotice(true)} data-testid="paywall-cta"
          style={{ width: "100%", background: C.blue, color: "#fff", border: "none", borderRadius: 999, padding: "14px 0", fontSize: 15, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
          Continue with {plan === "annual" ? "Annual" : "Monthly"}
        </button>

        {notice && (
          <div style={{ background: C.cardGray, borderRadius: 14, padding: "12px 14px", fontSize: 12.5, color: C.muted, lineHeight: 1.55, marginBottom: 10 }}>
            In-app checkout is launching shortly. Want WTR+ today? The{" "}
            <a href={BOTTLE_URL} target="_blank" rel="noreferrer" style={{ color: C.blueDark, fontWeight: 800 }}>
              Intelligent WTR Bottle
            </a>{" "}
            includes 12 months of WTR+ with your purchase.
          </div>
        )}

        <div style={{ fontSize: 11.5, color: C.muted, textAlign: "center", lineHeight: 1.5 }}>
          Hardware purchases include 12 months of WTR+.
        </div>
      </div>
    </div>
  );
}
